import { useNavigate } from 'react-router-dom';
import { formatRupees } from '@/lib/format';
import type { ServiceSummary } from '@shared/types/api';

export function ServiceCard({
  service,
  onClick,
}: {
  service: ServiceSummary;
  onClick?: () => void;
}) {
  const navigate = useNavigate();
  // Catalogue fees are Decimal(12,2) strings in rupees — plain float addition
  // is safe here (capped under ₹10,000,000 per side, no silent BigInt mixing).
  const startsAt =
    Number(service.defaultAssociateFeeRupees ?? service.defaultApplicationFeeRupees ?? 0) +
    Number(service.defaultPlatformFeeRupees ?? service.defaultServiceChargeRupees ?? 0);
  const totalAmount = Number(service.grandTotalRupees ?? 0) || startsAt;

  // Deterministic mock rating & purchase count based on service ID hash
  const rating = (4.0 + (service.id.charCodeAt(0) % 10) / 10).toFixed(1);

  return (
    <article
      onClick={() => {
        if (onClick) {
          onClick();
        } else {
          navigate(`/client/services/${service.id}`);
        }
      }}
      className="w-full min-w-0 cursor-pointer flex gap-4 rounded-xl border border-gray-150 bg-white p-3 hover:border-brand-200 hover:shadow-md transition-all duration-250 select-none items-center"
    >
      {/* Service Image with Rating badge */}
      <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 shrink-0 relative">
        {service.images && service.images[0] ? (
          <img src={service.images[0]} alt={service.name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-brand-50/50 text-brand-700 font-bold text-lg">
            {service.name.substring(0, 2).toUpperCase()}
          </div>
        )}

        {/* Rating Badge */}
        <div className="absolute top-1 left-1 bg-white/95 px-1.5 py-0.5 rounded-md flex items-center gap-0.5 text-xs font-bold text-gray-800 shadow-sm border border-gray-100 sm:text-[10px]">
          <span className="text-yellow-500">★</span>
          <span>{rating}</span>
        </div>
      </div>

      {/* Service Info */}
      <div className="flex-1 min-w-0 flex flex-col justify-between self-stretch py-0.5">
        <div>
          <h3 className="text-base font-bold text-gray-900 truncate leading-snug sm:text-base">
            {service.name}
          </h3>
          {service.description ? (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2 leading-relaxed sm:text-xs">
              {service.description}
            </p>
          ) : null}
        </div>

        {/* Pricing */}
        <div className="mt-2 pt-1">
          {service.isStartsFrom ? (
            <>
              <div className="text-xs font-semibold text-brand-700 sm:text-sm font-semibold">
                Starts from {formatRupees(service.startsFromRupees ?? 0)}
              </div>
              <div className="text-[10px] text-gray-400 font-medium">Variable Pricing</div>
            </>
          ) : (
            <>
              <div className="text-lg font-extrabold text-brand-700 sm:text-lg">
                {formatRupees(totalAmount)}
              </div>
              <div className="text-[10px] text-gray-400 font-medium">Total Amount (incl. GST)</div>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
